import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import type { InviteRoleCard, InviteRoleOption } from './types';

type UserRoleDialogProps = {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  title: string;
  email: string;
  onEmailChange?: (email: string) => void;
  emailDisabled?: boolean;
  selectedRole: InviteRoleOption;
  onRoleChange: (role: InviteRoleOption) => void;
  roleCards: InviteRoleCard[];
  onSubmit: () => void;
  submitLabel: string;
  submittingLabel: string;
  submitIcon: React.ComponentType<{ className?: string }>;
  isSubmitting: boolean;
  isSubmitDisabled?: boolean;
};

const UserRoleDialog = ({
  open,
  onOpenChange,
  title,
  email,
  onEmailChange,
  emailDisabled = false,
  selectedRole,
  onRoleChange,
  roleCards,
  onSubmit,
  submitLabel,
  submittingLabel,
  submitIcon: SubmitIcon,
  isSubmitting,
  isSubmitDisabled = false,
}: UserRoleDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] p-0 overflow-hidden">
        <DialogHeader className="border-b border-border/50 bg-muted/20 px-6 py-5">
          <DialogTitle className="text-2xl">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 px-6 py-5">
          <div className="space-y-2">
            <label htmlFor="user-role-dialog-email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="user-role-dialog-email"
              type="email"
              value={email}
              onChange={(event) => onEmailChange?.(event.target.value)}
              placeholder="user@example.com"
              disabled={emailDisabled}
            />
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium">Role</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {roleCards.map((role) => {
                const isActive = selectedRole === role.key;
                const RoleIcon = role.icon;
                return (
                  <button
                    key={role.key}
                    type="button"
                    onClick={() => onRoleChange(role.key)}
                    className={cn(
                      'flex flex-col items-center justify-center gap-2 rounded-xl border px-4 py-5 text-sm transition-colors',
                      isActive
                        ? 'border-primary/60 bg-primary/10 text-primary'
                        : 'border-border/60 bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    )}
                  >
                    <RoleIcon className="h-5 w-5" />
                    <span className="font-medium">{role.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitDisabled || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {submittingLabel}
                </>
              ) : (
                <>
                  <SubmitIcon className="h-4 w-4" />
                  {submitLabel}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserRoleDialog;
